import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, LogOut } from "lucide-react";
import {
  resolveConsumerRuntimeApiBase,
  resolveConsumerRuntimeConnectionUuid,
  resolveConsumerRuntimeEventsBase,
} from "../runtime-endpoints";
import { readConsumerRuntimeDeviceMode, readConsumerRuntimeTableCode } from "../runtime-device-mode";
import { trn, useI18n } from "../shared/i18n";
import { refreshBootstrap } from "../runtime-bootstrap";

// ✅ Definir interfaz para TerminalConfig
interface TerminalConfig {
  connectionUuid?: string | null;
  terminalId?: string | null;
  sellingPointId?: string | null;
  printerEnabled?: boolean;
  paymentTerminalEnabled?: boolean;
  paymentTerminalType?: string | null;
  receiptFormat?: string | null;
}

// ✅ Definir interfaz para Props sin `CartItem`
interface SettingsProps {
  setCart: React.Dispatch<React.SetStateAction<any[]>>; // 🔥 Mantiene la estructura sin definir `CartItem`
}

const Settings: React.FC<SettingsProps> = ({ setCart }) => {
  const navigate = useNavigate();
  const { language } = useI18n();
  const [connectionUuid] = useState<string>(resolveConsumerRuntimeConnectionUuid());
  const [loading, setLoading] = useState<boolean>(false);
  const [menuRefreshing, setMenuRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [menuMessage, setMenuMessage] = useState<string | null>(null);
  const [terminalConfig, setTerminalConfig] = useState<Partial<TerminalConfig> | null>(null);
  const [apiBaseUrl, setApiBaseUrl] = useState<string>("");
  const [eventsBaseUrl, setEventsBaseUrl] = useState<string>("");
  const deviceMode = readConsumerRuntimeDeviceMode();
  const tableCode = readConsumerRuntimeTableCode();

  useEffect(() => {
    if (!connectionUuid) {
      navigate("/setup");
      return;
    }
    fetchConfig();
  }, [navigate, connectionUuid]);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);

    try {
      const resolvedApiBaseUrl = resolveConsumerRuntimeApiBase(connectionUuid);
      const resolvedEventsBaseUrl = resolveConsumerRuntimeEventsBase(connectionUuid);
      setApiBaseUrl(resolvedApiBaseUrl);
      setEventsBaseUrl(resolvedEventsBaseUrl);
      const { payload } = await refreshBootstrap(connectionUuid, {
        language,
        refreshPlatformBranding: true,
      });
      const config: Partial<TerminalConfig> = (payload?.kioskConfig as Partial<TerminalConfig> | undefined) || {};
      setTerminalConfig(config);
      localStorage.setItem("terminalConfig", JSON.stringify(config));
    } catch (err) {
      setError(trn("app.consumer.settings.load_error", "Could not load the device configuration."));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    setError(null);
    setMenuMessage(null);
    setTerminalConfig(null);
    localStorage.removeItem("terminalConfig");
    fetchConfig();
  };

  const handleMenuRefresh = async () => {
    if (!connectionUuid) return;
    setMenuRefreshing(true);
    setError(null);
    setMenuMessage(null);

    try {
      const { payload } = await refreshBootstrap(connectionUuid, {
        language,
        refreshPlatformBranding: true,
      });
      const config: Partial<TerminalConfig> = (payload?.kioskConfig as Partial<TerminalConfig> | undefined) || {};
      setTerminalConfig(config);
      localStorage.setItem("terminalConfig", JSON.stringify(config));
      setMenuMessage(
        trn("app.consumer.settings.menu_refresh_success", "Menu updated successfully.")
      );
    } catch (_err) {
      setError(
        trn("app.consumer.settings.menu_refresh_error", "Could not update the menu.")
      );
    } finally {
      setMenuRefreshing(false);
    }
  };

  const handleResetTerminal = () => {
    // 🔥 Borra TODOS los datos almacenados en `localStorage`
    localStorage.clear();

    // ✅ Borra el estado del carrito para evitar que persista en memoria
    setCart([]);

    // ✅ Redirige a la configuración inicial
    navigate("/setup");

    // ✅ Recarga la página para asegurarse de que todo se reinicia
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  return (
    <div className="fullscreen-container">
      <div className="fullscreen-content">
        <h1 className="text-center mb-4">{trn("app.consumer.settings.title", "Device settings")}</h1>

        {loading ? (
          <p className="text-center">{trn("app.consumer.settings.loading", "Loading configuration...")}</p>
        ) : error ? (
          <p className="text-danger text-center">{error}</p>
        ) : (
          <div className="config-details">
            <p><strong>{trn("app.consumer.settings.connection_uuid", "Connection UUID")}:</strong> {terminalConfig?.connectionUuid ?? connectionUuid ?? trn("app.consumer.common.unknown", "Unknown")}</p>
            <p><strong>{trn("app.consumer.settings.selling_point_id", "Selling point ID")}:</strong> {terminalConfig?.sellingPointId ?? terminalConfig?.terminalId ?? trn("app.consumer.settings.not_configured", "Not configured")}</p>
            <p><strong>{trn("app.consumer.settings.mode", "Mode")}:</strong> {deviceMode === "table" ? trn("app.consumer.settings.table_mode", "Table") : trn("app.consumer.settings.kiosk_mode", "Kiosk")}</p>
            <p><strong>{trn("app.consumer.settings.table", "Table")}:</strong> {deviceMode === "table" ? tableCode || trn("app.consumer.settings.not_configured", "Not configured") : "—"}</p>
            <p><strong>{trn("app.consumer.settings.api_base", "API base")}:</strong> {apiBaseUrl || trn("app.consumer.settings.not_configured", "Not configured")}</p>
            <p><strong>{trn("app.consumer.settings.events_base", "Events base")}:</strong> {eventsBaseUrl || trn("app.consumer.settings.not_configured", "Not configured")}</p>
            <p><strong>{trn("app.consumer.settings.printer", "Printer")}:</strong> {terminalConfig?.printerEnabled ? trn("app.consumer.settings.active", "Active") : trn("app.consumer.settings.not_configured", "Not configured")}</p>
            <p><strong>{trn("app.consumer.settings.integrated_payment", "Integrated payment")}:</strong> {terminalConfig?.paymentTerminalEnabled ? terminalConfig?.paymentTerminalType || trn("app.consumer.common.yes", "Yes") : trn("app.consumer.common.no", "No")}</p>
            <p><strong>{trn("app.consumer.settings.format", "Format")}:</strong> {terminalConfig?.receiptFormat ?? trn("app.consumer.settings.not_configured", "Not configured")}</p>
          </div>
        )}

        {menuMessage ? <p className="text-success text-center mt-3">{menuMessage}</p> : null}

        <button className="btn btn-warning btn-lg w-100 mt-4" onClick={handleRefresh} disabled={loading}>
          <RefreshCw size={20} /> {trn("app.consumer.settings.reload", "Reload configuration")}
        </button>

        <button
          className="btn btn-primary btn-lg w-100 mt-3"
          onClick={() => void handleMenuRefresh()}
          disabled={loading || menuRefreshing}
        >
          <RefreshCw size={20} />
          {menuRefreshing
            ? trn("app.consumer.settings.menu_refreshing", "Updating menu...")
            : trn("app.consumer.settings.menu_refresh", "Update menu")}
        </button>

        <button className="btn btn-danger btn-lg w-100 mt-3" onClick={handleResetTerminal}>
          <LogOut size={20} /> {trn("app.consumer.settings.reset_device", "Change device (clear data)")}
        </button>

        <button className="btn btn-secondary btn-lg w-100 mt-3" onClick={() => navigate("/")}>
          {trn("app.consumer.common.back", "Back")}
        </button>
      </div>
    </div>
  );
};

export default Settings;
